import {
  Box,
  Grid,
  Stack,
  Toolbar,
  Typography,
  TextField,
  CircularProgress,
  Button,
  IconButton,
  Paper,
  Divider,
  Chip,
  Autocomplete,
  Badge,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API } from '../../../utils/Api';
import {
  CloseOutlined, KeyboardArrowLeft, LocalMallOutlined, Inventory2Outlined,
  ChecklistOutlined,
  AddShoppingCartOutlined,
  CategoryOutlined,
  ReceiptLongOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const normalizeUnit = (u = '') => String(u).trim().toUpperCase();

const isMlUnit = (u) => {
  const x = normalizeUnit(u);
  return x === 'ML' || x === 'MILLILITER' || x === 'MILLILITRE';
};

const isGramUnit = (u) => {
  const x = normalizeUnit(u);
  return x === 'GRAM' || x === 'GRAMS' || x === 'GM' || x === 'G' || x === 'GMS';
};

const getBaseUnitInfo = (unit) => {
  if (isMlUnit(unit)) return { baseUnit: 'L', factorToBase: 1 / 1000 };
  if (isGramUnit(unit)) return { baseUnit: 'KG', factorToBase: 1 / 1000 };
  return { baseUnit: normalizeUnit(unit) || '', factorToBase: 1 };
};

const toNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const roundTo = (n, dp = 4) => {
  const p = Math.pow(10, dp);
  return Math.round(n * p) / p;
};

const getConvertedRateToSend = (unit, enteredRateStr) => {
  const rateNum = toNumber(enteredRateStr);
  if (rateNum <= 0) return 0;

  if (isMlUnit(unit)) return roundTo(rateNum / 1000, 6);
  if (isGramUnit(unit)) return roundTo(rateNum / 1000, 6);
  return rateNum;
};

const LocalStock = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const outletData = JSON.parse(localStorage.getItem('outletData') || '{}');

  const [allRawMaterials, setAllRawMaterials] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [rates, setRates] = useState({});

  const [searchQuery, setSearchQuery] = useState('');

  const [vendor, setVendor] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [outletStockMaterials, setOutletStockMaterials] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('ALL');

  const normalize = (value) => String(value ?? '').toLowerCase().trim();

  const getRawMeta = (item) => {
    const raw = item?.rawMaterial || item || {};

    return {
      id: raw._id,
      name: raw.name || '',
      unit: raw.unit || '',
      mainCategoryId: raw.mainCategory?._id || '',
      mainCategoryName: raw.mainCategory?.name || '',
      subCategoryName: raw.subCategory?.name || '',
      currentStock: Number(item?.currentStock ?? raw?.currentStock ?? 0),
      stockInfo:
        item?.stockInfo ||
        (Number(item?.currentStock ?? raw?.currentStock ?? 0) <= 0
          ? 'Out of Stock'
          : 'In Stock'),
    };
  };

  const formatStock = (value) => {
    const n = Number(value || 0);
    return Number.isInteger(n) ? n : n.toFixed(2);
  };

  const fetchAllRawMaterials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API.BASE_URL}raw-material`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100000 },
      });

      const data = response?.data?.data;

      let list = [];
      if (Array.isArray(data?.rawMaterials)) list = data.rawMaterials;
      else if (Array.isArray(data?.totalRecords)) list = data.totalRecords;
      else if (Array.isArray(data?.dbResponse)) list = data.dbResponse;
      else if (Array.isArray(data)) list = data;

      setAllRawMaterials(list.filter((item) => item?.isDelete === false));
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch raw materials.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOutletCurrentStock = async () => {
    try {
      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            outletId: outletData?._id,
            limit: 100000,
          },
        }
      );

      const totalRecords = response?.data?.data?.totalRecords;

      if (
        typeof totalRecords === 'string' &&
        totalRecords.toLowerCase().includes('no records')
      ) {
        setOutletStockMaterials([]);
      } else {
        setOutletStockMaterials(Array.isArray(totalRecords) ? totalRecords : []);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch outlet stock.');
    }
  };

  const fetchAllVendors = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API.BASE_URL}vendor`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVendors(response?.data?.data?.vendors || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch vendors.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllRawMaterials();
    fetchOutletCurrentStock();
    fetchAllVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   if (!searchQuery.trim()) {
  //     setRawMaterials([]);
  //     return;
  //   }

  //   const q = searchQuery.toLowerCase();
  //   const filtered = allRawMaterials.filter((item) => {
  //     const { name } = getRawMeta(item);
  //     return name.toLowerCase().includes(q);
  //   });

  //   setRawMaterials(filtered);
  // }, [allRawMaterials, searchQuery]);

  const rawMaterialsWithStock = useMemo(() => {
    const stockMap = new Map();

    outletStockMaterials.forEach((item) => {
      const rawId = item?.rawMaterial?._id || item?._id;
      if (rawId) stockMap.set(rawId, item);
    });

    return allRawMaterials.map((raw) => {
      const stockItem = stockMap.get(raw._id);

      return {
        rawMaterial: raw,
        currentStock: Number(stockItem?.currentStock ?? 0),
        stockInfo:
          stockItem?.stockInfo ||
          (Number(stockItem?.currentStock ?? 0) <= 0 ? 'Out of Stock' : 'In Stock'),
      };
    });
  }, [allRawMaterials, outletStockMaterials]);

  const categories = useMemo(() => {
    const map = new Map();

    rawMaterialsWithStock.forEach((item) => {
      const { mainCategoryId, mainCategoryName } = getRawMeta(item);
      const key = mainCategoryId || mainCategoryName;

      if (key && !map.has(key)) {
        map.set(key, {
          id: key,
          name: mainCategoryName || 'Uncategorized',
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rawMaterialsWithStock]);

  const filteredRawMaterials = useMemo(() => {
    const q = normalize(searchQuery);
    const words = q ? q.split(/\s+/).filter(Boolean) : [];

    return rawMaterialsWithStock.filter((item) => {
      const meta = getRawMeta(item);
      const categoryKey = meta.mainCategoryId || meta.mainCategoryName;

      const matchesCategory =
        selectedCategory === 'ALL' || categoryKey === selectedCategory;

      if (!matchesCategory) return false;
      if (!words.length) return true;

      const haystack = normalize(
        [
          meta.name,
          meta.mainCategoryName,
          meta.subCategoryName,
          meta.unit,
          meta.currentStock,
          meta.stockInfo,
        ].join(' ')
      );

      return words.every((word) => haystack.includes(word));
    });
  }, [rawMaterialsWithStock, searchQuery, selectedCategory]);

  const updateSelectionForItem = (itemId, nextQty, nextRate) => {
    const qtyNum = parseFloat(nextQty);
    const rateNum = parseFloat(nextRate);

    const hasValidQty = !isNaN(qtyNum) && qtyNum > 0;
    const hasValidRate = !isNaN(rateNum) && rateNum > 0;

    setSelectedItems((prev) => {
      const exists = prev.includes(itemId);

      if (hasValidQty && hasValidRate) return exists ? prev : [...prev, itemId];
      return prev.filter((id) => id !== itemId);
    });
  };

  const handleQuantityChange = (itemId, inputValue) => {
    const numericValue = parseFloat(inputValue);

    setQuantities((prev) => {
      const updated = { ...prev };
      if (!inputValue || isNaN(numericValue) || numericValue <= 0) delete updated[itemId];
      else updated[itemId] = inputValue;
      return updated;
    });

    const currentRate = rates[itemId] ?? '';
    const nextQty = !inputValue || isNaN(numericValue) || numericValue <= 0 ? '' : inputValue;
    updateSelectionForItem(itemId, nextQty, currentRate);
  };

  const handleRateChange = (itemId, inputValue) => {
    const numericValue = parseFloat(inputValue);

    setRates((prev) => {
      const updated = { ...prev };
      if (!inputValue || isNaN(numericValue) || numericValue <= 0) delete updated[itemId];
      else updated[itemId] = inputValue;
      return updated;
    });

    const currentQty = quantities[itemId] ?? '';
    const nextRate = !inputValue || isNaN(numericValue) || numericValue <= 0 ? '' : inputValue;
    updateSelectionForItem(itemId, currentQty, nextRate);
  };

  const handleSubmit = async () => {
    if (!selectedItems.length) {
      toast.error('Please select at least one item by adding quantities.');
      return;
    }
    if (!vendor) {
      toast.error('Please select a vendor.');
      return;
    }
    if (!invoiceNo.trim()) {
      toast.error('Please enter invoice number.');
      return;
    }
    if (!invoiceFiles.length) {
      toast.error('Please upload an invoice file.');
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('localProcurement[outlet]', outletData?._id);
      formData.append('localProcurement[vendor]', vendor);
      formData.append('localProcurement[invoiceNo]', invoiceNo);

      invoiceFiles.forEach((file) => {
        formData.append('invoice_file', file);
      });

      const headerResp = await axios.post(`${API.BASE_URL}local-procurement`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const localProcurementId =
        headerResp?.data?.data?.dbResponse?._id ||
        headerResp?.data?.data?._id ||
        headerResp?.data?._id;

      if (!localProcurementId) throw new Error('localProcurementId not found in response.');

      // ✅ Qty sent as entered. Rate sent converted for ML/GRAMS.
      await Promise.all(
        selectedItems.map((itemId) => {
          const quantityStr = quantities[itemId];
          const rateStr = rates[itemId];

          if (!quantityStr) return Promise.resolve();

          // const wrapper = allRawMaterials.find((i) => getRawMeta(i).id === itemId);
          const wrapper = rawMaterialsWithStock.find((i) => getRawMeta(i).id === itemId);

          const { unit } = getRawMeta(wrapper);

          const convertedRate = getConvertedRateToSend(unit, rateStr);

          return axios.post(
            `${API.BASE_URL}local-procurement-item`,
            {
              localProcurementId,
              rawMaterial: itemId,
              quantity: String(quantityStr),      // ✅ as-entered (ML/GRAMS stays same)
              rate: String(convertedRate || 0),   // ✅ converted when needed
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        })
      );

      toast.success('Local procurement created and items added successfully.');
      setVendor('');
      setInvoiceNo('');
      setInvoiceFiles([]);
      setSelectedItems([]);
      setQuantities({});
      setRates({});
      fetchAllRawMaterials();
      fetchOutletCurrentStock();
      navigate('/local-stock-listing');
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.data?.message ||
        error?.response?.data?.data?.title ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to create local procurement.'
      );
    } finally {
      setLoading(false);
    }
  };

  const hasSelection = selectedItems.length > 0;

  const totalQuantity = useMemo(() => {
    return selectedItems.reduce((sum, id) => {
      const value = Number(quantities[id]);
      return sum + (!Number.isNaN(value) ? value : 0);
    }, 0);
  }, [selectedItems, quantities]);

  const selectedMaterialsMap = useMemo(() => {
    const map = new Map();
    rawMaterialsWithStock.forEach((item) => {
      const meta = getRawMeta(item);
      if (meta.id) map.set(meta.id, meta);
    });
    return map;
  }, [rawMaterialsWithStock]);

  const handleRemoveSelectedItem = (itemId) => {
    setSelectedItems((prev) => prev.filter((id) => id !== itemId));

    setQuantities((prev) => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });

    setRates((prev) => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top left, rgba(0,48,73,0.08), transparent 32%), linear-gradient(135deg, #f6f8fb 0%, #edf3f8 100%)',
      }}
    >
      <Toolbar />

      <Box sx={{ maxWidth: 1360, mx: 'auto', px: { xs: 2, md: 3 }, pb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            mb: 3,
            p: { xs: 2.2, md: 3 },
            borderRadius: 5,
            overflow: 'hidden',
            color: '#fff',
            background:
              'linear-gradient(135deg, #003049 0%, #07516f 55%, #0f6b8c 100%)',
            boxShadow: '0 18px 45px rgba(0,48,73,0.22)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  color: '#003049',
                  bgcolor: '#fff',
                  '&:hover': { bgcolor: '#eef6fb' },
                }}
              >
                <KeyboardArrowLeft />
              </IconButton>

              <Box
                sx={{
                  width: 58,
                  height: 58,
                  borderRadius: 4,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.26)',
                }}
              >
                <LocalMallOutlined sx={{ fontSize: 31 }} />
              </Box>

              <Box>
                <Typography variant="h5" fontWeight={900}>
                  Local Procurement
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.82)', mt: 0.5 }}
                >
                  Add raw materials, enter quantity and rate, then create local procurement.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                icon={<ChecklistOutlined />}
                label={`${selectedItems.length} Items`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.16)',
                  color: '#fff',
                  fontWeight: 800,
                  border: '1px solid rgba(255,255,255,0.22)',
                  '& .MuiChip-icon': { color: '#fff' },
                }}
              />

              <Chip
                label={`Total Qty: ${totalQuantity}`}
                sx={{
                  bgcolor: '#fff',
                  color: '#003049',
                  fontWeight: 900,
                }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 5,
                overflow: 'hidden',
                border: '1px solid #dce6ee',
                boxShadow: '0 16px 40px rgba(15,23,42,0.08)',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  pb: 1,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)',
                  borderBottom: '1px solid #e2eaf1',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1.5}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={900} color="#003049">
                      Raw Materials
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use search or category to filter.
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    icon={<SearchIcon />}
                    label={`${filteredRawMaterials.length} / ${rawMaterialsWithStock.length} materials`}
                    variant="outlined"
                    sx={{ fontWeight: 800, bgcolor: '#fff', fontSize: 12, px: 1 }}
                  />
                </Stack>

                <Grid container spacing={1.4} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} md={7}>
                    <TextField
                      fullWidth
                      placeholder="Search "
                      size="small"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                              <CloseOutlined fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 38,
                          borderRadius: 3,
                          backgroundColor: '#fff',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        label="Category"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        startAdornment={
                          <InputAdornment position="start">
                            <CategoryOutlined fontSize="small" />
                          </InputAdornment>
                        }
                        sx={{
                          height: 38,
                          borderRadius: 3,
                          backgroundColor: '#fff',
                        }}
                      >
                        <MenuItem value="ALL">All Categories</MenuItem>
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ p: 2.5, height: '62vh', overflowY: 'auto' }}>
                {loading ? (
                  <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="text.secondary">
                        Fetching raw materials with stock...
                      </Typography>
                    </Stack>
                  </Box>
                ) : filteredRawMaterials?.length > 0 ? (
                  <Grid container spacing={1.5}>
                    {filteredRawMaterials.map((item) => {
                      const {
                        id,
                        name,
                        unit,
                        mainCategoryName,
                        currentStock,
                        stockInfo,
                      } = getRawMeta(item);

                      if (!id) return null;

                      const isSelected = selectedItems.includes(id);
                      const isOutOfStock =
                        stockInfo === 'Out of Stock' || Number(currentStock) <= 0;

                      const rateInputUnit = isMlUnit(unit)
                        ? 'L'
                        : isGramUnit(unit)
                          ? 'KG'
                          : normalizeUnit(unit);

                      return (
                        <Grid item xs={12} key={id}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.6,
                              borderRadius: 3.5,
                              border: isSelected
                                ? '2px solid #003049'
                                : '1px solid #e2eaf1',
                              background: isSelected
                                ? 'linear-gradient(135deg, #eef8ff 0%, #ffffff 100%)'
                                : isOutOfStock
                                  ? 'linear-gradient(135deg, #fff7f8 0%, #ffffff 100%)'
                                  : '#ffffff',
                              display: 'flex',
                              alignItems: { xs: 'stretch', sm: 'center' },
                              justifyContent: 'space-between',
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: 1.5,
                              width: '100%',
                              boxSizing: 'border-box',
                              transition: '0.2s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 12px 28px rgba(15,23,42,0.09)',
                                borderColor: '#b7c9d8',
                              },
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                              <Badge color="primary" variant="dot" invisible={!isSelected}>
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 3,
                                    bgcolor: isSelected ? '#003049' : '#f0f5f8',
                                    color: isSelected ? '#fff' : '#607d8b',
                                    display: 'grid',
                                    placeItems: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Inventory2Outlined fontSize='30' />
                                </Box>
                              </Badge>

                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="caption"
                                  fontWeight={900}
                                  sx={{
                                    textTransform: 'capitalize',
                                    color: '#17212b',
                                    whiteSpace: 'normal',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 'unset',
                                    WebkitBoxOrient: 'vertical',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {name}
                                </Typography>

                                <Stack direction="row" spacing={1} alignItems="center" mt={0.7} flexWrap="wrap">
                                  {!!mainCategoryName && (
                                    <Chip
                                      size="small"
                                      label={mainCategoryName}
                                      sx={{
                                        height: 22,
                                        fontSize: 8,
                                        bgcolor: '#f5f8fb',
                                        fontWeight: 700,
                                        textTransform: 'capitalize',
                                      }}
                                    />
                                  )}
                                </Stack>
                              </Box>
                            </Stack>

                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              spacing={1.2}
                              alignItems={{ xs: 'stretch', md: 'center' }}
                              justifyContent="flex-end"
                              sx={{
                                width: { xs: '100%', md: '1oo%' },
                                minWidth: 0,
                                flexWrap: 'wrap',
                              }}
                            >
                              {/* <Chip
                                label={
                                  <>
                                    <span style={{ fontWeight: 600 }}>Stock:</span>{' '}
                                    {formatStock(currentStock)} {unit}
                                  </>
                                }
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  height: 28,
                                  fontSize: 11,
                                  borderRadius: 2,
                                  justifyContent: 'center',
                                  bgcolor: isOutOfStock ? '#fff1f2' : '#eef8f3',
                                  color: isOutOfStock ? '#991b1b' : '#166534',
                                  border: isOutOfStock ? '1px solid #fecaca' : '1px solid #ccebd9',
                                }}
                              /> */}

                              <TextField
                                size="small"
                                placeholder="Qty"
                                value={quantities[id] || ''}
                                onChange={(e) => handleQuantityChange(id, e.target.value)}
                                sx={{
                                  flex: { xs: '1 1 100%', md: '0 0 76px' },
                                  minWidth: 0,
                                  '& .MuiOutlinedInput-root': {
                                    height: 42,
                                    borderRadius: 2.5,
                                    backgroundColor: '#fff',
                                    fontWeight: 800,
                                  },
                                  '& input': { textAlign: 'right', fontWeight: 800 },
                                }}
                              />

                              <TextField
                                size="small"
                                placeholder={rateInputUnit ? `Rate / ${rateInputUnit}` : 'Rate'}
                                value={rates[id] || ''}
                                onChange={(e) => handleRateChange(id, e.target.value)}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Typography variant="caption" color="text.secondary">
                                        ₹
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  flex: { xs: '1 1 100%', md: '0 0 130px' },
                                  minWidth: 0,
                                  '& .MuiOutlinedInput-root': {
                                    height: 42,
                                    borderRadius: 2.5,
                                    backgroundColor: '#fff',
                                    fontWeight: 800,
                                  },
                                  '& input': { textAlign: 'right', fontWeight: 800 },
                                }}
                              />
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                    <Stack alignItems="center" spacing={0.8}>
                      <Typography variant="body1" fontWeight={900} color="#003049">
                        No material found
                      </Typography>
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        Try changing the search text or selected category.
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 5,
                overflow: 'hidden',
                border: '1px solid #dce6ee',
                boxShadow: '0 16px 40px rgba(15,23,42,0.08)',
                position: { md: 'sticky' },
                top: { md: 88 },
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  pb: 1,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fbfd 100%)',
                  borderBottom: '1px solid #e2eaf1',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={900} color="#003049">
                      Procurement Summary
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Add vendor, invoice and review items.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 3,
                      bgcolor: hasSelection ? '#003049' : '#eef2f5',
                      color: hasSelection ? '#fff' : '#90a4ae',
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <ReceiptLongOutlined />
                  </Box>
                </Stack>

                <Grid container spacing={1.5} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={vendors}
                      getOptionLabel={(option) => option.name || ''}
                      value={vendors.find((v) => v._id === vendor) || null}
                      onChange={(e, newValue) => setVendor(newValue?._id || '')}
                      renderInput={(params) => (
                        <TextField {...params} label="Vendor" placeholder="Select vendor" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Invoice No."
                      size="small"
                      value={invoiceNo}
                      onChange={(e) => setInvoiceNo(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{
                        height: 42,
                        textTransform: 'none',
                        borderRadius: 2.5,
                        fontWeight: 800,
                      }}
                    >
                      {invoiceFiles.length
                        ? `${invoiceFiles.length} file(s) selected`
                        : 'Upload Invoice'}
                      <input
                        hidden
                        type="file"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          setInvoiceFiles(files);
                        }}
                      />
                    </Button>
                  </Grid>
                </Grid>

                {hasSelection && (
                  <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                    <Chip
                      label={`${selectedItems.length} Selected`}
                      sx={{ fontWeight: 800 }}
                      color={hasSelection ? 'primary' : 'default'}
                    />
                    <Chip
                      label={`${totalQuantity} Qty`}
                      variant="outlined"
                      sx={{ fontWeight: 800, bgcolor: '#fff' }}
                    />
                  </Stack>
                )}
              </Box>

              <Box sx={{ p: 2.2, maxHeight: '50vh', overflowY: 'auto' }}>
                {hasSelection ? (
                  <Stack spacing={1.3}>
                    {selectedItems.map((itemId, index) => {
                      const meta = selectedMaterialsMap.get(itemId);
                      if (!meta) return null;

                      const enteredRate = rates[itemId] || '';
                      const convertedRatePreview = enteredRate
                        ? getConvertedRateToSend(meta.unit, enteredRate)
                        : '';

                      return (
                        <Paper
                          key={itemId}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: 3.5,
                            border: '1px solid #e2eaf1',
                            background: 'linear-gradient(135deg, #f9fcff 0%, #ffffff 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.3,
                          }}
                        >
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: '50%',
                              bgcolor: '#e7f0f6',
                              color: '#003049',
                              display: 'grid',
                              placeItems: 'center',
                              fontSize: 13,
                              fontWeight: 900,
                              flexShrink: 0,
                            }}
                          >
                            {index + 1}
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={900}
                              sx={{
                                textTransform: 'capitalize',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: '#17212b',
                              }}
                            >
                              {meta.name}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                              Qty: {quantities[itemId]} {normalizeUnit(meta.unit)}
                            </Typography>

                            {enteredRate ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Rate: ₹{enteredRate} | Send: ₹{convertedRatePreview}
                              </Typography>
                            ) : null}
                          </Box>

                          <IconButton
                            onClick={() => handleRemoveSelectedItem(itemId)}
                            size="small"
                            sx={{
                              color: '#d32f2f',
                              bgcolor: '#fff1f1',
                              '&:hover': { bgcolor: '#ffe0e0' },
                            }}
                          >
                            <CloseOutlined fontSize="small" />
                          </IconButton>
                        </Paper>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box sx={{ height: 210, display: 'grid', placeItems: 'center' }}>
                    <Stack alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 74,
                          height: 74,
                          borderRadius: '50%',
                          bgcolor: '#f0f5f8',
                          display: 'grid',
                          placeItems: 'center',
                        }}
                      >
                        <Inventory2Outlined sx={{ fontSize: 34, color: '#90a4ae' }} />
                      </Box>

                      <Typography variant="body2" fontWeight={900} color="#003049">
                        No items selected
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        textAlign="center"
                        sx={{ maxWidth: 260 }}
                      >
                        Enter quantity and rate for any raw material to create procurement.
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </Box>

              <Divider />

              <Box sx={{ p: 2.2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    sx={{
                      height: 46,
                      textTransform: 'none',
                      borderRadius: 3,
                      fontWeight: 900,
                      borderColor: '#cfdbe5',
                      color: '#003049',
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!hasSelection || loading}
                    sx={{
                      height: 46,
                      borderRadius: 3,
                      fontWeight: 900,
                      fontSize: 11,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #003049 0%, #0f5f7f 100%)',
                      boxShadow: '0 10px 22px rgba(0,48,73,0.22)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #002433 0%, #0b4d68 100%)',
                      },
                      '&.Mui-disabled': {
                        background: '#c7cdd2',
                        color: '#fff',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {loading ? 'Processing...' : 'Create Procurement'}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default LocalStock;
